import React, { useEffect, useState, useRef } from "react";
import { header } from "../constants/header";
import { Link, useLocation } from "react-router-dom";
import { FiMenu, FiX } from "react-icons/fi";
import { ChevronDown } from "lucide-react";
import "../assets/css/navbar.css";
import logo from "../assets/img/logo-mantic.png";
import khmer from "../assets/img/flag/khmer.png";
import china from "../assets/img/flag/china.png";
import english from "../assets/img/flag/english.png";
import { useTranslation } from "react-i18next";
import i18n from "../locales/index.js";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [openDesktop, setOpenDesktop] = useState(false);
  const [openMobile, setOpenMobile] = useState(false);

  const desktopDropdownRef = useRef(null);
  const mobileDropdownRef = useRef(null);
  const location = useLocation();

  const { t } = useTranslation();

  const languages = [
    { code: "en", name: "English", flag: english },
    { code: "kh", name: "ខ្មែរ", flag: khmer },
    { code: "ch", name: "中文", flag: china },
  ];

  const currentLangCode = i18n.language || "en";
  const current =
    languages.find((l) => l.code === currentLangCode) || languages[0];

  // Scroll effect
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Click outside dropdown
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        desktopDropdownRef.current &&
        !desktopDropdownRef.current.contains(e.target)
      ) {
        setOpenDesktop(false);
      }
      if (
        mobileDropdownRef.current &&
        !mobileDropdownRef.current.contains(e.target)
      ) {
        setOpenMobile(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Change language
  const changeLanguage = (code) => {
    i18n.changeLanguage(code);
    localStorage.setItem("language", code);
    setOpenDesktop(false);
    setOpenMobile(false);
  };

  useEffect(() => {
    const savedLang = localStorage.getItem("language");
    if (savedLang && savedLang !== i18n.language) {
      i18n.changeLanguage(savedLang);
    }
  }, []);

  return (
    <nav
      className={`content-center min-h-[90px] fixed top-0 left-0 w-full 
  bg-coffee-dark/95 backdrop-blur-md z-[99] transition-all duration-300
  ${isScrolled ? "shadow-soft py-1" : "py-3"}`}
      data-aos="fade-down"
    >
      <div className="max-w-screen-2xl mx-auto flex items-center justify-between px-3">
        {/* Logo */}
        <Link to="/" className="flex items-center">
          <img
            src={logo}
            alt="Logo"
            className="h-16 w-auto sm:h-18 md:h-20 lg:h-24"
          />
        </Link>

        {/* Mobile Controls */}
        <div
          className="flex items-center space-x-3 md:hidden"
          ref={mobileDropdownRef}
        >
          {/* Language Dropdown (Mobile) */}
          <div className="relative">
            <button
              onClick={() => setOpenMobile(!openMobile)}
              className="border border-coffee-light/50 text-cream px-2 py-1 rounded-md flex items-center gap-1 text-sm hover:bg-coffee-medium transition-colors"
            >
              <img
                src={current.flag}
                alt={current.name}
                className="w-5 h-5 rounded-sm object-cover"
              />
              <ChevronDown className="w-4 h-4" />
            </button>

            {openMobile && (
              <div className="absolute right-0 mt-2 bg-white border border-gray-200 rounded-md shadow-lg z-10">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => changeLanguage(lang.code)}
                    className="flex justify-center items-center w-full px-4 py-2 text-sm text-coffee-dark hover:bg-cream-dark transition-colors"
                  >
                    <img
                      src={lang.flag}
                      alt={lang.name}
                      className="w-5 h-5 rounded-sm object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Mobile Menu Toggle */}
          <button
            className="text-2xl uppercase text-cream hover:text-accent-gold transition-colors"
            onClick={() => setIsOpen(!isOpen)}
          >
            {isOpen ? <FiX /> : <FiMenu />}
          </button>
        </div>

        {/* Desktop Menu */}
        <ul className="hidden md:flex md:space-x-4 xl:space-x-10 uppercase font-medium text-sm xl:text-lg">
          {header.map((item, i) => {
            const isActive = location.pathname === item.path;
            return (
              <li key={i} className="relative group">
                <Link
                  to={item.path}
                  className={`${isActive ? "after:w-full text-accent-gold font-semibold" : "after:w-0 text-cream hover:text-accent-gold"} after:content-[''] after:absolute after:left-0 after:bottom-[-4px] after:h-[2px] after:bg-accent-gold after:transition-all after:duration-300 group-hover:after:w-full transition-colors`}
                >
                  {t(item.key)} {/* ✅ Translate menu titles */}
                </Link>
              </li>
            );
          })}
        </ul>

        {/* Desktop Right Section */}
        <div
          className="hidden md:flex items-center space-x-4 relative"
          ref={desktopDropdownRef}
        >
          {/* Language Dropdown */}
          <div className="relative">
            <button
              onClick={() => setOpenDesktop(!openDesktop)}
              className="border border-coffee-light/50 rounded-md text-cream px-3 py-1 flex items-center gap-2 hover:bg-coffee-medium transition-colors"
            >
              <img
                src={current.flag}
                alt={current.name}
                className="w-5 h-5 rounded-sm object-cover"
              />
              <span>{current.name}</span>
              <ChevronDown className="w-4 h-4" />
            </button>

            {openDesktop && (
              <div className="absolute mt-2 w-32 bg-white border border-gray-200 rounded-md shadow-lg z-10">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => changeLanguage(lang.code)}
                    className="flex items-center w-full px-3 py-2 text-sm text-coffee-dark hover:bg-cream-dark transition-colors"
                  >
                    <img
                      src={lang.flag}
                      alt={lang.name}
                      className="w-5 h-5 rounded-sm object-cover mr-2"
                    />
                    {lang.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Phone Button */}
          {/* <button
            onClick={() => (window.location.href = "tel:+85512345678")}
            className="text-black rounded-md flex items-center gap-2"
          >
            <FiPhone className="border border-yellow-400 bg-yellow-400 p-2 text-gray-50 text-xl w-8 h-8 rounded-full hover:bg-yellow-500 cursor-pointer" />
            <span className="md:text-sm font-medium text-gray-700">
              +855 81 333 000
            </span>
          </button> */}
        </div>
      </div>

      {/* Mobile Dropdown Menu */}
      {isOpen && (
        <div className="md:hidden bg-coffee-dark/95 backdrop-blur-md md:px-2 md-pb-2 md:space-y-0 px-4 pb-4 space-y-2 text-sm uppercase border-t border-coffee-medium">
          <ul className="space-y-2">
            {header.map((item, i) => {
              const isActive = location.pathname === item.path;
              return (
                <li key={i}>
                  <Link
                    to={item.path}
                    className={`block transition ${
                      isActive
                        ? "text-accent-gold font-semibold"
                        : "text-cream font-medium hover:text-accent-gold"
                    }`}
                    onClick={() => setIsOpen(false)}
                  >
                    {t(item.key)}
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
